import React, { useState } from 'react';
import { Card } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import { ScrollArea } from './components/ui/scroll-area';
import { SignalDetailDialog } from './components/SignalDetailDialog';
import {
  Search,
  RefreshCw,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './components/ui/dialog';

export default function BusinessTrends() {
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [showInsightPopup, setShowInsightPopup] = useState(false);
  const [selectedInsight, setSelectedInsight] = useState(null);

  // Mock data - replace with real data in production
  const signals = [
    {
      id: '1',
      type: 'HIRING',
      headline: 'Stripe Hires 300 Engineers in AI Infra',
      summary: 'Major expansion in AI infrastructure team',
      tags: ['Hiring', 'AI', 'Infrastructure'],
      urgency: 8,
      impact: 7,
      timestamp: '2h ago',
      company: 'Stripe'
    },
    {
      id: '2',
      type: 'PRICING',
      headline: 'Airtable Reduces Pricing Tiers from 4 to 2',
      summary: 'Simplified pricing structure to improve conversion',
      tags: ['Pricing', 'GTM'],
      urgency: 6,
      impact: 5,
      timestamp: '4h ago',
      company: 'Airtable'
    },
    {
      id: '3',
      type: 'M&A',
      headline: 'Datadog Acquires AI Monitoring Startup',
      summary: 'Strategic acquisition to enhance AI observability',
      tags: ['M&A', 'AI', 'Product'],
      urgency: 7,
      impact: 8,
      timestamp: '1d ago',
      company: 'Datadog'
    }
  ];

  const metrics = [
    {
      metric: 'ML Hiring',
      change: 38,
      category: 'Hiring',
      reason: 'Increased focus on AI capabilities'
    },
    {
      metric: 'Series A Layoffs',
      change: -20,
      category: 'Cutback',
      reason: 'Market correction in early-stage funding'
    },
    {
      metric: 'On-prem Pricing',
      change: 25,
      category: 'Pricing',
      reason: 'Growing enterprise security requirements'
    }
  ];

  const marketInsights = [
    {
      title: 'AI Adoption Trends',
      description: 'Companies are increasingly adopting AI technologies to enhance productivity and innovation.',
      source: 'Industry Reports',
      timestamp: '1h ago'
    },
    {
      title: 'Remote Work Impact',
      description: 'Remote work is reshaping hiring strategies and office space utilization.',
      source: 'LinkedIn',
      timestamp: '2h ago'
    },
    {
      title: 'Sustainability Initiatives',
      description: 'Businesses are focusing on sustainability to meet regulatory and consumer demands.',
      source: 'Green Business Journal',
      timestamp: '3h ago'
    }
  ];

  const companyMoves = [
    {
      company: 'Stripe',
      logo: '/logos/stripe.png',
      action: 'Partnership',
      color: 'text-blue-400',
      description: 'Stripe partners with OpenAI to streamline AI payments.',
      source: 'https://news.stripe.com/openai-partnership',
      timestamp: '1h ago',
    },
    {
      company: 'Airtable',
      logo: '/logos/airtable.png',
      action: 'Launch',
      color: 'text-green-400',
      description: 'Airtable launches new workflow automation suite.',
      source: 'https://blog.airtable.com/automation-suite',
      timestamp: '2h ago',
    },
    {
      company: 'Datadog',
      logo: '/logos/datadog.png',
      action: 'Expansion',
      color: 'text-red-400',
      description: 'Datadog expands into APAC region with new data centers.',
      source: 'https://datadog.com/news/apac-expansion',
      timestamp: '3h ago',
    },
  ];

  return (
    <div lang="en" className="dark">
      <div className="bg-black text-white min-h-[73vh] overflow-hidden overflow-y-auto rounded-[5px]">
        <div className="container mx-auto p-6 space-y-6">
          {/* Header */}
          <div className="space-y-2">
            <h1 className="text-3xl font-bold">Business Trends Dashboard</h1>
            <p className="text-muted-foreground">
              See how companies across your space are shifting behavior in hiring, pricing, expansion, and operations.
            </p>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search Business Signals"
                  className="pl-10 w-[300px]"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Button variant="outline">Compare</Button>
              <Button variant="ghost">View Archives</Button>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">Last updated: {new Date().toLocaleTimeString()}</span>
              <Button variant="ghost" size="icon">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Filter Strip */}
          <div className="flex space-x-2 overflow-x-auto pb-2">
            {['ALL', 'GROWTH', 'CUTBACK', 'HIRING', 'PRICING', 'M&A', 'GTM', 'PRODUCT'].map((filter) => (
              <Button
                key={filter}
                variant={activeFilter === filter ? 'default' : 'outline'}
                onClick={() => setActiveFilter(filter)}
                className="whitespace-nowrap"
              >
                {filter}
              </Button>
            ))}
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-12 gap-6 h-[calc(72vh-250px)]">
            {/* Left Panel - Signal Feed */}
            <Card className="col-span-4 p-4">
              <h2 className="text-lg font-semibold mb-4">Signal Feed</h2>
              <ScrollArea className="h-full">
                <div className="space-y-4">
                  {signals.map((signal) => (
                    <Card
                      key={signal.id}
                      className="p-4 hover:bg-accent/50 transition-colors cursor-pointer"
                      onClick={() => setSelectedSignal(signal)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            {signal.type === 'GROWTH' ? (
                              <TrendingUp className="h-4 w-4 text-green-500" />
                            ) : (
                              <TrendingDown className="h-4 w-4 text-red-500" />
                            )}
                            <h3 className="font-medium">{signal.headline}</h3>
                          </div>
                          <p className="text-sm text-muted-foreground">{signal.summary}</p>
                          <div className="flex flex-wrap gap-2">
                            {signal.tags.map((tag) => (
                              <Badge key={tag} variant="secondary">{tag}</Badge>
                            ))}
                          </div>
                        </div>
                        <span className="text-sm text-muted-foreground">{signal.timestamp}</span>
                      </div>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            </Card>

            {/* Middle Panel - What Companies Are Doing */}
            <Card className="col-span-4 p-4">
              <h2 className="text-lg font-semibold mb-4">What Companies Are Doing</h2>
              <div className="space-y-4">
                {metrics.map((metric, index) => (
                  <Card key={index} className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium">{metric.metric}</h3>
                        <p className="text-sm text-muted-foreground">{metric.reason}</p>
                      </div>
                      <span className={`text-sm ${metric.change > 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {metric.change > 0 ? '+' : ''}{metric.change}%
                      </span>
                    </div>
                  </Card>
                ))}
              </div>
            </Card>

            {/* Right Panel - Key Company Moves */}
            <Card className="col-span-4 p-4">
              <h2 className="text-lg font-semibold mb-4">Key Company Moves</h2>
              <div className="space-y-4">
                {companyMoves.map((move, index) => (
                  <Card key={index} className="p-4 flex items-center gap-4 bg-[#111] border border-[#222]">
                    <div className="flex-shrink-0">
                      <img src={move.logo} alt={move.company} width={40} height={40} className="rounded-full bg-white p-1" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold ${move.color}`}>{move.action}</span>
                        <span className="text-xs text-muted-foreground">{move.timestamp}</span>
                      </div>
                      <div className="text-sm text-white">{move.description}</div>
                      <a href={move.source} target="_blank" rel="noopener noreferrer" className="text-blue-300 text-xs underline mt-1 inline-block">Source</a>
                    </div>
                  </Card>
                ))}
              </div>
            </Card>
          </div>

          {/* Signal Detail Dialog */}
          {selectedSignal && (
            <SignalDetailDialog
              signal={selectedSignal}
              open={!!selectedSignal}
              onOpenChange={() => setSelectedSignal(null)}
            />
          )}

          {/* Market Insight Popup */}
          {showInsightPopup && selectedInsight && (
            <Dialog open={showInsightPopup} onOpenChange={setShowInsightPopup}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{selectedInsight}</DialogTitle>
                </DialogHeader>
                <p>Data and sources for {selectedInsight} will be displayed here.</p>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
    </div>
  );
} 
